/**
 * ViewAs Context
 *
 * Provides admin preview mode support. When an admin navigates to
 * /?viewAs=<parentId>, this context provides the effective parent ID
 * for data queries, enabling admins to see the parent portal as if
 * they were that parent.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';

interface ViewAsContextValue {
  /** The effective parent ID to use for data queries */
  effectiveParentId: string | null;
  /** Whether we're in admin preview mode */
  isViewingAs: boolean;
  /** The original viewAs param value */
  viewAsParentId: string | null;
}

const ViewAsContext = createContext<ViewAsContextValue>({
  effectiveParentId: null,
  isViewingAs: false,
  viewAsParentId: null,
});

export const useViewAs = () => useContext(ViewAsContext);

export const ViewAsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const { profile, isAdmin } = useAuth();

  const profileId = profile?.id || null;
  const isAdminFlag = isAdmin();

  const value = useMemo(() => {
    const viewAsParam = searchParams.get('viewAs');
    const viewAsParentId = isAdminFlag && viewAsParam ? viewAsParam : null;
    const effectiveParentId = viewAsParentId || profileId || null;

    return {
      effectiveParentId,
      isViewingAs: !!viewAsParentId,
      viewAsParentId,
    };
  }, [searchParams, profileId, isAdminFlag]);

  return (
    <ViewAsContext.Provider value={value}>
      {children}
    </ViewAsContext.Provider>
  );
};
